// @ts-check
// Kanban (doc 06, seção 6): uma coluna por opção do campo SELECT de agrupamento, cabeçalho com
// tag colorida, contagem e agregações (groupBy com várias agregações, spec 04), cartões arrastáveis
// entre colunas com atualização otimista do valor e da posição. Relações ONE_TO_MANY do card
// (ex.: alunos da família) aparecem como chips, com a etapa do filho quando ela diverge. Paridade (contrato
// 08-contrato-kanban-tabela): barra de ações da seleção, agregações escolhidas no cabeçalho, menu da coluna, colunas
// vazias ocultas, modo compacto, largura das colunas, campos do cartão e edição no próprio cartão. Papel restrito por
// etapa (contrato de equipe, seção 3.2): só as colunas de Ver e, no fim, as de Pode passar para soltar o card.
import { html } from 'htm/preact';
import { resolveStageTransition } from './stage-transition.js';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { findManyRecords, findOneRecord, findRecordsByIds, groupByRecords } from '../../data/rest.js';
import { createLiveRecordList, mutationCounterState, patchRecordLocally, remoteListChangeState, updateRecordOptimistically } from '../../data/record-store.js';
import { currentUserPermissionsState, fieldsById, objectsByNameSingular } from '../../data/metadata-store.js';
import {
  FIELD_TYPE,
  findOption,
  getLabelIdentifierField,
  getRelationIdFieldName,
  isFieldDisplayable,
  isFieldValueEmpty,
  isOneToManyRelation,
} from '../../lib/field-metadata.js';
import { getChildIdentifyingRelationField, getChildStage, getChildStageFields, getInverseRelationField } from '../../lib/related-records.js';
import { formatNumber } from '../../lib/format.js';
import { computePositionBetween } from '../../lib/utils.js';
import { canUpdateObjectRecords, funnelStageFieldKind, visibleFunnelStages } from '../../lib/permissions.js';
import { RecordChip, Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { Checkbox, EmptyState, Skeleton } from '../../ui/feedback.js';
import { LightIconButton } from '../../ui/button.js';
import { getViewQuery } from '../views/view-state.js';
import { openRecord, registerRecordListProvider } from '../side-panel/open-record.js';
import { enqueueSnackbar, showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { hasTranslation, t, translateMetadataLabel } from '../../i18n/index.js';
import { CardScheduleButton } from '../calendar/card-schedule-button.js';
import { CARDS_PER_COLUMN, appendUniqueRecords, loadNextColumnPage, toColumnPageState } from './board-column-pages.js';
import { NOT_A_LEAD_STAGE, canClassifyAsNotLead, classifyRecordAsNotLead, supportsNotLead } from './not-lead.js';
import { RecordSelectionActions } from './record-selection-actions.js';
import { RecordContextMenu, selectionForContextMenu } from '../command-menu/record-context-menu.js';
import { BoardColumnHeader } from './board-column-header.js';
import { BoardCardField } from './board-card-field.js';
import { formatBoardAggregate, getAggregateShortLabel } from './aggregate-menu.js';
import { persistViewPatch } from '../views/view-options.js';
import { drilldownModeOf } from '../../lib/drilldown-links.js';
import {
  NO_VALUE_GROUP_KEY, KANBAN_COLUMN_DEFAULT_WIDTH, applyStageScopeToBoardGroups, clampKanbanColumnWidth, getDisplayedGroups, getRecordGroups, moveRecordsToGroup,
} from './view-layout.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../../data/metadata-store.js').View} View
 * @typedef {{ key: string, value: string | null, label: string, color: string, isHandoff?: boolean }} BoardColumn
 * @typedef {{ operation: string, field: FieldMetadata, parameter: string }} BoardAggregate
 * @typedef {{ count: number, aggregateValues: unknown[] }} BoardGroup
 * @typedef {import('./board-column-pages.js').ColumnPageState} ColumnPageState
 * @typedef {{ field: FieldMetadata, targetObject: ObjectMetadata | undefined, identifyingField: FieldMetadata | undefined,
 *   identifyingObject: ObjectMetadata | undefined, stageFields: ReturnType<typeof getChildStageFields> }} RelatedListConfig
 */

// o cartão mostra até 8 campos; a escolha e a ordem ficam nos viewFields da view kanban
const MAX_CARD_FIELDS = 8;
const NO_VALUE_KEY = NO_VALUE_GROUP_KEY;

/**
 * Colunas visíveis na ordem da view (Não é lead fica escondida sem viewGroup próprio).
 * @param {View} view
 * @param {FieldMetadata} groupField
 * @returns {BoardColumn[]}
 */
function getBoardColumns(view, groupField) {
  return getRecordGroups(view.viewGroups, groupField)
    .filter((group) => group.isVisible)
    .map((group) => {
      if (group.value === null) return { key: NO_VALUE_KEY, value: null, label: t('board.noValue'), color: 'transparent' };
      const option = findOption(groupField, group.value);
      return { key: group.key, value: group.value, label: translateMetadataLabel(option?.label ?? group.value), color: option?.color ?? 'gray' };
    });
}

/**
 * Agregações do cabeçalho: `kanbanAggregates` da view ou, sem elas, a operação única antiga.
 * COUNT é a própria contagem de cards, já exibida.
 * @param {View} view
 * @returns {BoardAggregate[]}
 */
function getBoardAggregates(view) {
  const configured = view.kanbanAggregates?.length
    ? view.kanbanAggregates
    : view.kanbanAggregateOperation
      ? [{ operation: view.kanbanAggregateOperation, fieldMetadataId: view.kanbanAggregateOperationFieldMetadataId ?? '', subFieldName: null }]
      : [];
  /** @type {BoardAggregate[]} */
  const aggregates = [];
  for (const item of configured) {
    const field = item.fieldMetadataId ? fieldsById.value.get(item.fieldMetadataId) : undefined;
    if (!item.operation || item.operation === 'COUNT' || !field) continue;
    const subFieldName = item.subFieldName ?? (field.type === FIELD_TYPE.CURRENCY ? 'amountMicros' : null);
    const path = subFieldName ? `${field.name}.${subFieldName}` : field.name;
    aggregates.push({ operation: item.operation, field, parameter: `${item.operation}:${path}` });
  }
  return aggregates;
}

/**
 * "12 famílias" quando o catálogo tem o rótulo de contagem do objeto; senão só o número.
 * @param {ObjectMetadata} objectMetadata
 * @param {number} count
 */
function formatCardCount(objectMetadata, count) {
  const key = `board.cardCount.${objectMetadata.nameSingular}`;
  return hasTranslation(key) ? t(key, { count }) : formatNumber(count);
}

/**
 * @param {ObjectMetadata} objectMetadata
 * @param {FieldMetadata} field
 * @returns {RelatedListConfig}
 */
function getRelatedListConfig(objectMetadata, field) {
  const targetObject = objectsByNameSingular.value.get(field.relation?.targetObjectNameSingular ?? '');
  const inverseField = getInverseRelationField(field, targetObject);
  const identifyingField = getChildIdentifyingRelationField(targetObject, inverseField?.name);
  const identifyingObject = identifyingField ? objectsByNameSingular.value.get(identifyingField.relation?.targetObjectNameSingular ?? '') : undefined;
  return { field, targetObject, identifyingField, identifyingObject, stageFields: getChildStageFields(targetObject, '') };
}

/**
 * @param {{ objectMetadata: ObjectMetadata, view: View, onTotalCountChange?: (count: number | null) => void, onRequestCreate?: (prefill: Record<string, unknown>) => void,
 *   onHiddenCountChange?: (count: number) => void }} props onHiddenCountChange: registros em colunas ocultas (aviso do chip do painel)
 */
export function RecordBoard({ objectMetadata, view, onTotalCountChange, onRequestCreate, onHiddenCountChange }) {
  const groupField = view.mainGroupByFieldMetadataId ? fieldsById.value.get(view.mainGroupByFieldMetadataId) : undefined;
  const liveList = useMemo(() => createLiveRecordList(objectMetadata.nameSingular), [objectMetadata.nameSingular, view.id]);
  useEffect(() => () => liveList.dispose(), [liveList]);
  const records = liveList.records.value;

  const [isLoading, setIsLoading] = useState(true);
  const [groups, setGroups] = useState(/** @type {Map<string, BoardGroup>} */ (new Map()));
  const [relatedLabels, setRelatedLabels] = useState(/** @type {Map<string, any>} */ (new Map()));
  const [draggedRecordId, setDraggedRecordId] = useState(/** @type {string | null} */ (null));
  const [dropTarget, setDropTarget] = useState(/** @type {{ columnKey: string, index: number } | null} */ (null));
  const [selectedIds, setSelectedIds] = useState(/** @type {Set<string>} */ (new Set()));
  // cursor de cada coluna para carregar os cartões além dos primeiros 30
  const [columnPages, setColumnPages] = useState(/** @type {Map<string, ColumnPageState>} */ (new Map()));
  const [editingCell, setEditingCell] = useState(/** @type {{ recordId: string, fieldId: string } | null} */ (null));
  // largura durante o arraste da alça; ao soltar vai para a view
  const [resizingWidth, setResizingWidth] = useState(/** @type {number | null} */ (null));
  const [reloadToken, setReloadToken] = useState(0);
  const requestIdRef = useRef(0);
  // recarga em silêncio quando outra pessoa cria cartões (contrato 08 de navegação, seção 6)
  const silentReloadRef = useRef(false);
  // clique direito no card (contrato 08-contrato-comandos-atalhos, seção 3)
  const [contextMenu, setContextMenu] = useState(/** @type {{ x: number, y: number } | null} */ (null));

  const query = getViewQuery(view);
  const aggregates = getBoardAggregates(view);
  const aggregateParameter = aggregates.map((aggregate) => aggregate.parameter).join(',');
  const viewColumns = groupField ? getBoardColumns(view, groupField) : [];
  const permissions = currentUserPermissionsState.value;
  const stageKind = groupField ? funnelStageFieldKind(objectMetadata.nameSingular, groupField.name) : null;
  const stageScope = applyStageScopeToBoardGroups(
    viewColumns.map((column, index) => ({ key: column.key, value: column.value, isVisible: true, position: index })),
    {
      kind: stageKind,
      visibleStages: visibleFunnelStages(permissions),
      handoffStages: groupField ? getRecordGroups([], groupField).map((group) => group.value).filter((value) => value !== null && permissions?.funnelStageScope?.handoffStageValues.includes(value)) : [],
      countOf: (key) => groups.get(key)?.count,
    },
  );
  const scopedKeys = new Set(stageScope.groups.map((group) => group.key));
  // etapa do card: as colunas fora de Ver nem são pedidas; etapa do aluno: a contagem decide quais aparecem
  const columns = stageKind === 'CARD' ? viewColumns.filter((column) => scopedKeys.has(column.key)) : viewColumns;
  /** @type {BoardColumn[]} */
  const handoffColumns = groupField ? stageScope.handoffValues.map((value) => {
    const option = findOption(groupField, value);
    return { key: `handoff:${value}`, value, label: translateMetadataLabel(option?.label ?? value), color: option?.color ?? 'gray', isHandoff: true };
  }) : [];
  const labelField = getLabelIdentifierField(objectMetadata);
  const canUpdate = canUpdateObjectRecords(objectMetadata);
  const cardFields = [...view.viewFields]
    .filter((viewField) => viewField.isVisible)
    .sort((first, second) => first.position - second.position)
    .map((viewField) => fieldsById.value.get(viewField.fieldMetadataId))
    .filter((field) => field && field.id !== labelField?.id && field.id !== groupField?.id && isFieldDisplayable(field))
    .slice(0, MAX_CARD_FIELDS);
  const relatedListConfigs = new Map(
    cardFields
      .filter((field) => isOneToManyRelation(field))
      .map((field) => {
        const config = getRelatedListConfig(objectMetadata, field);
        return [field.id, { ...config, stageFields: getChildStageFields(config.targetObject, groupField?.name ?? '') }];
      }),
  );

  // Filhos vêm com depth=1 sem as próprias relações; o nome do aluno é buscado em lote.
  const missingRelatedIds = [];
  for (const config of relatedListConfigs.values()) {
    if (!config.identifyingField || !config.identifyingObject) continue;
    const idFieldName = getRelationIdFieldName(config.identifyingField);
    for (const record of records) {
      for (const child of Array.isArray(record[config.field.name]) ? record[config.field.name] : []) {
        const relatedId = child[idFieldName];
        if (relatedId && !child[config.identifyingField.name] && !relatedLabels.has(relatedId)) missingRelatedIds.push(`${config.identifyingObject.nameSingular}:${relatedId}`);
      }
    }
  }
  const missingRelatedKey = [...new Set(missingRelatedIds)].sort().join(',');
  useEffect(() => {
    if (!missingRelatedKey) return;
    let isCancelled = false;
    /** @type {Map<string, string[]>} */
    const idsByObject = new Map();
    for (const entry of missingRelatedKey.split(',')) {
      const [objectName, id] = entry.split(':');
      idsByObject.set(objectName, [...(idsByObject.get(objectName) ?? []), id]);
    }
    Promise.all(
      [...idsByObject.entries()].map(([objectName, ids]) => {
        const relatedObject = objectsByNameSingular.value.get(objectName);
        return relatedObject ? findRecordsByIds(relatedObject, ids) : Promise.resolve([]);
      }),
    )
      .then((results) => {
        if (isCancelled) return;
        setRelatedLabels((current) => {
          const next = new Map(current);
          for (const relatedRecord of results.flat()) next.set(relatedRecord.id, relatedRecord);
          return next;
        });
      })
      .catch(() => {});
    return () => {
      isCancelled = true;
    };
  }, [missingRelatedKey]);

  const columnFilter = (/** @type {BoardColumn} */ column) => {
    const columnPredicate = { field: /** @type {FieldMetadata} */ (groupField).name, operator: column.value === null ? 'is' : 'eq', value: column.value === null ? 'NULL' : column.value };
    return query.filter ? { and: [query.filter, columnPredicate] } : columnPredicate;
  };
  // mostrar de novo uma coluna oculta precisa carregar os cartões dela
  const columnKeysSignature = columns.map((column) => column.key).join(',');

  useEffect(() => {
    if (!groupField) return;
    const requestId = ++requestIdRef.current;
    const isSilent = silentReloadRef.current;
    silentReloadRef.current = false;
    if (!isSilent) {
      setIsLoading(true);
      setSelectedIds(new Set());
    }
    Promise.all(
      columns.map((column) =>
        findManyRecords(objectMetadata, { filter: columnFilter(column), orderBy: query.orderBy, limit: CARDS_PER_COLUMN, depth: 1, drilldown: query.drilldown }),
      ),
    )
      .then((results) => {
        if (requestId !== requestIdRef.current) return;
        liveList.records.value = results.flatMap((result) => result.records);
        setColumnPages(new Map(columns.map((column, index) => [column.key, toColumnPageState(results[index])])));
      })
      .catch((error) => {
        if (requestId === requestIdRef.current) showErrorSnackbar(error);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setIsLoading(false);
      });
  }, [objectMetadata.id, view.id, query.key, groupField?.id, liveList, reloadToken, columnKeysSignature]);

  // cartão novo de outra pessoa: relê as colunas sem esqueleto, fora de arraste e de edição
  const remoteListVersion = remoteListChangeState.value[objectMetadata.nameSingular] ?? 0;
  const remoteListVersionRef = useRef(remoteListVersion);
  useEffect(() => {
    if (remoteListVersion === remoteListVersionRef.current) return;
    remoteListVersionRef.current = remoteListVersion;
    // quem já carregou mais cartões numa coluna não perde o que carregou
    const hasLoadedMore = columns.some((column) => records.filter((record) => groupKeyOf(record) === column.key).length > CARDS_PER_COLUMN);
    if (isLoading || draggedRecordId || editingCell || hasLoadedMore) return;
    silentReloadRef.current = true;
    setReloadToken((token) => token + 1);
  }, [remoteListVersion]);

  useEffect(() => {
    if (!groupField) return;
    let isCancelled = false;
    groupByRecords(objectMetadata, { groupByFieldName: groupField.name, filter: query.filter, aggregate: aggregateParameter || null, drilldown: query.drilldown })
      .then((result) => {
        if (isCancelled) return;
        /** @type {Map<string, BoardGroup>} */
        const nextGroups = new Map();
        let total = 0;
        let hidden = 0;
        // colunas escondidas (Não é lead) não entram no total do quadro
        const visibleKeys = new Set(columns.map((column) => column.key));
        for (const group of result) {
          const key = group.value === null || group.value === undefined || group.value === '' ? NO_VALUE_KEY : String(group.value);
          const aggregateValues = Array.isArray(group.aggregateValues) ? group.aggregateValues : aggregates.length > 0 ? [group.aggregateValue ?? null] : [];
          nextGroups.set(key, { count: Number(group.count ?? 0), aggregateValues: aggregateValues.map((value) => (value === undefined ? null : value)) });
          if (visibleKeys.has(key)) total += Number(group.count ?? 0);
          else hidden += Number(group.count ?? 0);
        }
        setGroups(nextGroups);
        onTotalCountChange?.(total);
        onHiddenCountChange?.(hidden);
      })
      .catch(() => {});
    return () => {
      isCancelled = true;
    };
  }, [objectMetadata.id, view.id, query.key, groupField?.id, aggregateParameter, mutationCounterState.value, reloadToken, columnKeysSignature]);

  // Esc limpa a seleção quando não há menu ou diálogo aberto (eles tratam o próprio Esc)
  useEffect(() => {
    if (selectedIds.size === 0) return undefined;
    /** @param {KeyboardEvent} event */
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector('[data-dropdown-surface], .modal-backdrop, .modal')) return;
      setSelectedIds(new Set());
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedIds.size]);

  if (!groupField) {
    return html`<div class="record-table-empty"><${EmptyState} icon="IconLayoutKanban" title=${t('board.noGroupField')} /></div>`;
  }

  /** @param {BoardColumn} column */
  const loadMoreCards = async (column) => {
    const state = columnPages.get(column.key);
    if (!state?.hasNextPage || state.isLoadingMore) return;
    const requestId = requestIdRef.current;
    setColumnPages((current) => new Map(current).set(column.key, { ...state, isLoadingMore: true }));
    try {
      const page = await loadNextColumnPage(
        (options) => findManyRecords(objectMetadata, options),
        { filter: columnFilter(column), orderBy: query.orderBy, drilldown: query.drilldown },
        state,
      );
      if (requestId !== requestIdRef.current) return;
      liveList.records.value = appendUniqueRecords(liveList.records.value, page.records);
      setColumnPages((current) => new Map(current).set(column.key, page.state));
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      showErrorSnackbar(error);
      setColumnPages((current) => new Map(current).set(column.key, { ...state, isLoadingMore: false }));
    }
  };

  // sem coluna Não é lead visível, o quadro oferece uma área de soltar no fim e a ação no card
  const hasNotLeadAction = supportsNotLead(objectMetadata) && groupField.name === 'stage' && !columns.some((column) => column.key === NOT_A_LEAD_STAGE);
  const groupKeyOf = (/** @type {any} */ record) => (isFieldValueEmpty(groupField, record[groupField.name]) ? NO_VALUE_KEY : String(record[groupField.name]));

  /** @param {string} recordId */
  const refreshRecord = (recordId) => findOneRecord(objectMetadata, recordId, { depth: 1 }).then((freshRecord) => {
    if (freshRecord) patchRecordLocally(objectMetadata.nameSingular, recordId, freshRecord);
  });

  /**
   * Ajuste imediato da contagem e da soma das colunas; o groupBy confirma em seguida.
   * @param {any} record
   * @param {string} fromKey
   * @param {string | null} toKey null quando o registro sai do quadro (exclusão)
   */
  const adjustGroupsLocally = (record, fromKey, toKey) => {
    const contributions = aggregates.map((aggregate) => (aggregate.operation === 'SUM' ? getAggregateContribution(record, aggregate.field) : 0));
    const shift = (/** @type {BoardGroup | undefined} */ group, /** @type {number} */ direction) => ({
      count: Math.max(0, (group?.count ?? 0) + direction),
      aggregateValues: aggregates.map((aggregate, index) => {
        const current = group?.aggregateValues[index] ?? null;
        if (aggregate.operation !== 'SUM') return current;
        return Number(current ?? 0) + direction * contributions[index];
      }),
    });
    setGroups((current) => {
      const next = new Map(current);
      next.set(fromKey, shift(next.get(fromKey), -1));
      if (toKey !== null) next.set(toKey, shift(next.get(toKey), 1));
      return next;
    });
  };

  /** @param {any} record */
  const classifyNotLead = async (record) => {
    const sourceKey = groupKeyOf(record);
    const isClassified = await classifyRecordAsNotLead(objectMetadata, record);
    if (isClassified) adjustGroupsLocally(record, sourceKey, NOT_A_LEAD_STAGE);
  };

  const recordsByColumn = (/** @type {BoardColumn} */ column) =>
    records
      .filter((record) => (column.value === null ? isFieldValueEmpty(groupField, record[groupField.name]) : record[groupField.name] === column.value))
      .sort((first, second) => Number(first.position ?? 0) - Number(second.position ?? 0));

  // coluna do cartão aberto vira a lista do anterior e próximo da página do registro ("n de total" na coluna)
  /** @param {BoardColumn} column @returns {import('../../lib/record-navigation.js').RecordListContext} */
  const buildColumnContext = (column) => {
    const pageState = columnPages.get(column.key);
    const columnRecords = recordsByColumn(column);
    return {
      objectNameSingular: objectMetadata.nameSingular,
      recordIds: columnRecords.map((record) => record.id),
      totalCount: groups.get(column.key)?.count ?? columnRecords.length,
      hasNextPage: Boolean(pageState?.hasNextPage),
      endCursor: pageState?.endCursor ?? null,
      filter: columnFilter(column),
      orderBy: query.orderBy,
      drilldown: query.drilldown,
      viewName: view.name ?? null,
      groupLabel: translateMetadataLabel(column.label),
    };
  };
  const columnContextRef = useRef(buildColumnContext);
  columnContextRef.current = buildColumnContext;
  const boardRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  useEffect(() => registerRecordListProvider({
    element: () => boardRef.current,
    getContext: (recordId) => {
      const record = records.find((candidate) => candidate.id === recordId);
      const column = record && groupField ? columns.find((candidate) => (candidate.value === null
        ? isFieldValueEmpty(groupField, record[groupField.name]) : record[groupField.name] === candidate.value)) : undefined;
      return column ? columnContextRef.current(column) : null;
    },
  }), [records, columns.length]);

  /**
   * @param {BoardColumn} column
   * @param {DragEvent} event
   */
  const handleDragOver = (column, event) => {
    if (!draggedRecordId) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    const columnElement = /** @type {HTMLElement} */ (event.currentTarget);
    const cardElements = [...columnElement.querySelectorAll('[data-board-card]')].filter(
      (element) => /** @type {HTMLElement} */ (element).dataset.recordId !== draggedRecordId,
    );
    let index = cardElements.length;
    for (let cardIndex = 0; cardIndex < cardElements.length; cardIndex += 1) {
      const rect = cardElements[cardIndex].getBoundingClientRect();
      if (event.clientY < rect.top + rect.height / 2) {
        index = cardIndex;
        break;
      }
    }
    if (dropTarget?.columnKey !== column.key || dropTarget.index !== index) setDropTarget({ columnKey: column.key, index });
  };

  /**
   * @param {BoardColumn} column
   */
  const handleDrop = async (column) => {
    const recordId = draggedRecordId;
    const target = dropTarget;
    setDraggedRecordId(null);
    setDropTarget(null);
    if (!recordId || !target || target.columnKey !== column.key) return;
    const record = records.find((candidate) => candidate.id === recordId);
    if (!record) return;
    const columnRecords = recordsByColumn(column).filter((candidate) => candidate.id !== recordId);
    const previous = columnRecords[target.index - 1];
    const next = columnRecords[target.index];
    const position = computePositionBetween(previous ? Number(previous.position) : null, next ? Number(next.position) : null);
    const sourceKey = groupKeyOf(record);
    const resolved = await resolveStageTransition(objectMetadata, record, { apiPatch: { [groupField.name]: column.value, position }, localPatch: { [groupField.name]: column.value, position } });
    // cancelar o diálogo deixa o card onde estava
    if (!resolved) return;
    if (sourceKey !== column.key) adjustGroupsLocally(record, sourceKey, column.key);
    updateRecordOptimistically(objectMetadata, recordId, resolved.apiPatch, resolved.localPatch)
      .then(() => {
        // O motor recalcula filhos e totais do card (etapa efetiva, contagens, valor).
        if (sourceKey !== column.key) return refreshRecord(recordId);
        return undefined;
      })
      .catch(() => {
        if (sourceKey !== column.key) adjustGroupsLocally(record, column.key, sourceKey);
      });
  };

  /**
   * Soltar na coluna de Pode passar: confirma, grava e o card sai do quadro (o record-store tira das listas).
   * @param {BoardColumn} column
   */
  const handleHandoffDrop = async (column) => {
    const record = records.find((candidate) => candidate.id === draggedRecordId);
    setDraggedRecordId(null);
    setDropTarget(null);
    if (!record || column.value === null) return;
    const sourceKey = groupKeyOf(record);
    const resolved = await resolveStageTransition(objectMetadata, record, { apiPatch: { [groupField.name]: column.value }, localPatch: { [groupField.name]: column.value } });
    if (!resolved) return;
    try {
      await updateRecordOptimistically(objectMetadata, record.id, resolved.apiPatch, resolved.localPatch);
      adjustGroupsLocally(record, sourceKey, null);
    } catch {
      // o snackbar de erro já saiu e o card volta para a coluna
    }
  };

  /** @param {any} record @param {import('./field-input.js').FieldPatch} requestedPatch */
  const commitCardPatch = async (record, requestedPatch) => {
    const patch = await resolveStageTransition(objectMetadata, record, requestedPatch);
    if (!patch) return;
    updateRecordOptimistically(objectMetadata, record.id, patch.apiPatch, patch.localPatch).catch(() => {});
  };

  // Mudar a etapa pela barra: um card por vez, cada um com o diálogo da etapa quando ele pedir algo
  /** @param {string | null} value */
  const moveSelectionToStage = async (value) => {
    const selected = records.filter((record) => selectedIds.has(record.id));
    const targetKey = value === null ? NO_VALUE_KEY : value;
    const result = await moveRecordsToGroup({
      records: selected,
      fieldName: groupField.name,
      value,
      resolve: (record, patch) => resolveStageTransition(objectMetadata, record, patch),
      update: async (record, patch) => {
        const sourceKey = groupKeyOf(record);
        await updateRecordOptimistically(objectMetadata, record.id, patch.apiPatch, patch.localPatch);
        adjustGroupsLocally(record, sourceKey, targetKey);
        refreshRecord(record.id).catch(() => {});
      },
    });
    const handled = new Set([...result.moved, ...result.unchanged].map((record) => record.id));
    setSelectedIds((current) => new Set([...current].filter((id) => !handled.has(id))));
    if (result.moved.length > 0) showSuccessSnackbar(t('boardTable.board.movedCards', { count: result.moved.length }));
    if (result.failed.length > 0 || result.cancelled.length > 0) {
      enqueueSnackbar({ message: t('boardTable.board.notMovedCards', { count: result.failed.length + result.cancelled.length }), variant: 'warning' });
    }
  };

  // carregando, as contagens ainda não vieram e nenhuma coluna some; no arraste todas ficam para receber o card
  const keepKeys = new Set(draggedRecordId ? columns.map((column) => column.key) : []);
  const displayedColumns = getDisplayedGroups(
    columns.map((column, index) => ({ key: column.key, value: column.value, isVisible: true, position: index })),
    (key) => (isLoading ? undefined : groups.get(key)?.count ?? records.filter((record) => groupKeyOf(record) === key).length),
    Boolean(view.shouldHideEmptyGroups),
    keepKeys,
  )
    .filter((group) => stageKind !== 'CHILD' || scopedKeys.has(group.key))
    .map((group) => /** @type {BoardColumn} */ (columns.find((column) => column.key === group.key)));
  const columnWidth = resizingWidth ?? view.kanbanColumnWidth ?? KANBAN_COLUMN_DEFAULT_WIDTH;

  /** @param {PointerEvent} event */
  const startResize = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = columnWidth;
    let latest = startWidth;
    /** @param {PointerEvent} moveEvent */
    const handleMove = (moveEvent) => {
      latest = clampKanbanColumnWidth(startWidth + moveEvent.clientX - startX);
      setResizingWidth(latest);
    };
    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      setResizingWidth(null);
      if (latest !== startWidth) persistViewPatch(view, { kanbanColumnWidth: latest });
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  };

  const selectedRecords = records.filter((record) => selectedIds.has(record.id));

  return html`<div class="record-board-container" ref=${boardRef}>
    <div class="record-board" data-compact=${view.isCompact ? 'true' : undefined} data-resizing=${resizingWidth !== null ? 'true' : undefined}
      style=${{ '--record-board-column-width': `${columnWidth}px` }}>
    ${displayedColumns.map((column, columnIndex) => {
      const columnRecords = recordsByColumn(column);
      const group = groups.get(column.key);
      const count = group?.count ?? columnRecords.length;
      const aggregateLabels = aggregates
        .map((aggregate, index) => {
          const value = group?.aggregateValues[index];
          if (value === null || value === undefined) return null;
          return { key: aggregate.parameter, text: formatBoardAggregate(aggregate.field, aggregate.operation, value), title: `${getAggregateShortLabel(aggregate.operation)}: ${translateMetadataLabel(aggregate.field.label)}` };
        })
        .filter(Boolean);
      const visibleRecords = draggedRecordId ? columnRecords.filter((record) => record.id !== draggedRecordId) : columnRecords;
      return html`<div
        key=${column.key}
        class="board-column"
        data-column-key=${column.key}
        data-drop-active=${dropTarget?.columnKey === column.key ? 'true' : undefined}
        onDragOver=${(event) => handleDragOver(column, event)}
        onDragLeave=${(event) => {
          if (!(/** @type {HTMLElement} */ (event.currentTarget).contains(/** @type {Node} */ (event.relatedTarget)))) setDropTarget(null);
        }}
        onDrop=${(event) => {
          event.preventDefault();
          handleDrop(column);
        }}
      >
        <${BoardColumnHeader}
          objectMetadata=${objectMetadata}
          view=${view}
          groupField=${groupField}
          column=${column}
          isFirst=${columnIndex === 0}
          isLast=${columnIndex === displayedColumns.length - 1}
          countLabel=${formatCardCount(objectMetadata, count)}
          aggregates=${aggregates}
          aggregateLabels=${aggregateLabels}
          canCreate=${canUpdate && drilldownModeOf(query.drilldown) !== 'SNAPSHOT'}
          onAddNew=${() => onRequestCreate?.({ [groupField.name]: column.value })}
          onResizeStart=${startResize}
        />
        <div class="board-column-cards">
          ${isLoading
            ? [0, 1].map((index) => html`<div key=${index} class="board-card board-card-skeleton"><${Skeleton} width="70%" height=${14} /><${Skeleton} width="50%" height=${12} /></div>`)
            : visibleRecords.map((record, index) => html`
                ${dropTarget?.columnKey === column.key && dropTarget.index === index ? html`<div class="board-drop-indicator"></div>` : null}
                <${BoardCard}
                  key=${record.id}
                  objectMetadata=${objectMetadata}
                  record=${record}
                  fields=${cardFields}
                  groupField=${groupField}
                  relatedListConfigs=${relatedListConfigs}
                  relatedLabels=${relatedLabels}
                  isCompact=${Boolean(view.isCompact)}
                  isSelected=${selectedIds.has(record.id)}
                  editingFieldId=${editingCell?.recordId === record.id ? editingCell.fieldId : null}
                  onStartEdit=${(/** @type {string} */ fieldId) => setEditingCell({ recordId: record.id, fieldId })}
                  onStopEdit=${() => setEditingCell(null)}
                  onCommit=${(/** @type {import('./field-input.js').FieldPatch} */ patch) => commitCardPatch(record, patch)}
                  onToggleSelected=${(checked) => {
                    const next = new Set(selectedIds);
                    if (checked) next.add(record.id);
                    else next.delete(record.id);
                    setSelectedIds(next);
                  }}
                  onDragStart=${(event) => {
                    event.dataTransfer?.setData('text/plain', record.id);
                    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
                    // Esconde a origem só depois que o navegador capturou a imagem do arraste.
                    requestAnimationFrame(() => setDraggedRecordId(record.id));
                  }}
                  onDragEnd=${() => {
                    setDraggedRecordId(null);
                    setDropTarget(null);
                  }}
                  onOpen=${(/** @type {MouseEvent} */ event) => openRecord({
                    objectNameSingular: objectMetadata.nameSingular, recordId: record.id, listContext: buildColumnContext(column), source: /** @type {Element} */ (event.currentTarget),
                  })}
                  onClassifyNotLead=${hasNotLeadAction && canClassifyAsNotLead(objectMetadata, record) ? () => classifyNotLead(record) : null}
                  onContextMenu=${(/** @type {MouseEvent} */ event) => {
                    if (editingCell || /** @type {HTMLElement} */ (event.target).closest('input, textarea, [contenteditable="true"]')) return;
                    event.preventDefault();
                    const { ids, keepsSelection } = selectionForContextMenu(selectedIds, record.id);
                    if (!keepsSelection) setSelectedIds(ids);
                    setContextMenu({ x: event.clientX, y: event.clientY });
                  }}
                />
              `)}
          ${dropTarget?.columnKey === column.key && dropTarget.index >= visibleRecords.length ? html`<div class="board-drop-indicator"></div>` : null}
          ${!isLoading && columnPages.get(column.key)?.hasNextPage
            ? html`<${BoardColumnLoadMore}
                isLoading=${Boolean(columnPages.get(column.key)?.isLoadingMore)}
                remainingCount=${Math.max(0, count - columnRecords.length)}
                onLoadMore=${() => loadMoreCards(column)}
              />`
            : null}
          ${canUpdate
            ? html`<button type="button" class="board-column-new" onClick=${() => onRequestCreate?.({ [groupField.name]: column.value })}>
                <${Icon} name="IconPlus" size=${16} />
                <span>${t('board.new')}</span>
              </button>`
            : null}
        </div>
      </div>`;
    })}
    ${canUpdate ? handoffColumns.map((column) => html`<div
        key=${column.key}
        class="board-handoff-column"
        data-testid="board-handoff-column"
        data-stage=${column.value}
        data-drop-active=${dropTarget?.columnKey === column.key ? 'true' : undefined}
        data-dragging=${draggedRecordId ? 'true' : undefined}
        onDragOver=${(/** @type {DragEvent} */ event) => {
          if (!draggedRecordId) return;
          event.preventDefault();
          if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
          if (dropTarget?.columnKey !== column.key) setDropTarget({ columnKey: column.key, index: 0 });
        }}
        onDragLeave=${() => setDropTarget(null)}
        onDrop=${(/** @type {DragEvent} */ event) => {
          event.preventDefault();
          handleHandoffDrop(column);
        }}
      >
        <${Icon} name="IconLogout" size=${16} />
        <span class="board-handoff-title">${t('boardTable.handoffColumn', { stage: column.label })}</span>
        <${Tag} color=${column.color} text=${column.label} />
      </div>`) : null}
    ${hasNotLeadAction && draggedRecordId
      ? html`<div
          class="board-not-lead-drop"
          data-testid="board-not-lead-drop"
          data-drop-active=${dropTarget?.columnKey === NOT_A_LEAD_STAGE ? 'true' : undefined}
          onDragOver=${(/** @type {DragEvent} */ event) => {
            event.preventDefault();
            if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
            if (dropTarget?.columnKey !== NOT_A_LEAD_STAGE) setDropTarget({ columnKey: NOT_A_LEAD_STAGE, index: 0 });
          }}
          onDragLeave=${() => setDropTarget(null)}
          onDrop=${(/** @type {DragEvent} */ event) => {
            event.preventDefault();
            const record = records.find((candidate) => candidate.id === draggedRecordId);
            setDraggedRecordId(null);
            setDropTarget(null);
            if (record) classifyNotLead(record);
          }}
        >
          <${Icon} name="IconUserOff" size=${20} />
          <span class="board-not-lead-drop-title">${t('notLead.dropZone')}</span>
          <span class="board-not-lead-drop-hint">${t('notLead.dropZoneHint')}</span>
        </div>`
      : null}
    </div>
    ${contextMenu && selectedIds.size > 0
      ? html`<${RecordContextMenu}
          position=${contextMenu}
          onClose=${() => setContextMenu(null)}
          target=${{
            objectMetadata, view, count: selectedIds.size, isTrashMode: false, isAllMatchingSelected: false,
            resolveSelectedIds: async () => [...selectedIds],
            onChanged: () => setReloadToken((token) => token + 1),
            onDeleted: (/** @type {string[]} */ ids) => {
              const deleted = new Set(ids);
              for (const record of selectedRecords.filter((candidate) => deleted.has(candidate.id))) adjustGroupsLocally(record, groupKeyOf(record), null);
              liveList.records.value = liveList.records.value.filter((record) => !deleted.has(record.id));
              setSelectedIds(new Set());
            },
            onClear: () => setSelectedIds(new Set()),
            stageOptions: groupField.type === FIELD_TYPE.SELECT ? [...columns, ...handoffColumns] : null,
            onMoveToStage: groupField.type === FIELD_TYPE.SELECT ? moveSelectionToStage : null,
          }}
        />`
      : null}
    ${selectedIds.size > 0
      ? html`<${RecordSelectionActions}
          objectMetadata=${objectMetadata}
          view=${view}
          selectedCount=${selectedIds.size}
          isAllMatchingSelected=${false}
          resolveSelectedIds=${async () => [...selectedIds]}
          onClear=${() => setSelectedIds(new Set())}
          onChanged=${() => setReloadToken((token) => token + 1)}
          onDeleted=${(/** @type {string[]} */ ids) => {
            const deleted = new Set(ids);
            for (const record of selectedRecords.filter((candidate) => deleted.has(candidate.id))) adjustGroupsLocally(record, groupKeyOf(record), null);
            liveList.records.value = liveList.records.value.filter((record) => !deleted.has(record.id));
            setSelectedIds(new Set());
          }}
          stageOptions=${groupField.type === FIELD_TYPE.SELECT ? [...columns, ...handoffColumns] : null}
          onMoveToStage=${groupField.type === FIELD_TYPE.SELECT ? moveSelectionToStage : null}
        />`
      : null}
  </div>`;
}

/**
 * Fim da coluna com mais cartões no servidor: carrega sozinho quando aparece na rolagem e também por clique.
 * @param {{ isLoading: boolean, remainingCount: number, onLoadMore: () => void }} props
 */
function BoardColumnLoadMore({ isLoading, remainingCount, onLoadMore }) {
  const buttonRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  useEffect(() => {
    const element = buttonRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onLoadMoreRef.current();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return html`<button
    ref=${buttonRef}
    type="button"
    class="board-column-load-more"
    data-testid="board-column-load-more"
    disabled=${isLoading}
    onClick=${onLoadMore}
  >
    ${isLoading ? t('board.loadingMore') : remainingCount > 0 ? t('board.loadMoreCount', { count: remainingCount }) : t('board.loadMore')}
  </button>`;
}

/**
 * @param {any} record
 * @param {FieldMetadata} field
 */
function getAggregateContribution(record, field) {
  const value = record[field.name];
  if (field.type === FIELD_TYPE.CURRENCY) return Number(value?.amountMicros ?? 0);
  return Number(value ?? 0);
}

/**
 * Chips dos filhos (ex.: alunos) com a etapa própria quando ela difere da etapa do card.
 * @param {{ config: RelatedListConfig, record: any, groupField: FieldMetadata, relatedLabels: Map<string, any> }} props
 */
function BoardCardRelatedList({ config, record, groupField, relatedLabels }) {
  const children = Array.isArray(record[config.field.name]) ? record[config.field.name] : [];
  if (children.length === 0) {
    return html`<span class="field-display-empty">${translateMetadataLabel(config.field.label)}</span>`;
  }
  const stageField = config.stageFields.effectiveField ?? config.stageFields.overrideField;
  return html`<span class="board-card-related-list">
    ${children.map((child) => {
      const relatedId = config.identifyingField ? child[getRelationIdFieldName(config.identifyingField)] : null;
      const relatedRecord = config.identifyingField ? child[config.identifyingField.name] ?? (relatedId ? relatedLabels.get(relatedId) : null) : null;
      const childStage = stageField ? getChildStage(child, record, groupField.name, config.stageFields) : null;
      const stageOption = childStage && childStage !== record[groupField.name] && stageField ? findOption(stageField, childStage) : null;
      return html`<span key=${child.id} class="board-card-related-item" data-child-stage=${childStage ?? undefined}>
        ${relatedRecord
          ? html`<${RecordChip} objectMetadata=${config.identifyingObject} record=${relatedRecord} />`
          : html`<${RecordChip} objectMetadata=${config.targetObject} record=${child} />`}
        ${stageOption ? html`<${Tag} color=${stageOption.color} text=${translateMetadataLabel(stageOption.label)} className="board-card-related-stage" />` : null}
      </span>`;
    })}
  </span>`;
}

/**
 * Cartão do kanban: título, ações e campos escolhidos na view. No modo compacto o corpo fica recolhido até a pessoa
 * abrir o cartão pelo olho (doc 06, seção 6.3); clicar num campo editável abre o editor no próprio cartão.
 * @param {{ objectMetadata: ObjectMetadata, record: any, fields: any[], groupField: FieldMetadata, relatedListConfigs: Map<string, RelatedListConfig>,
 *   relatedLabels: Map<string, any>, isSelected: boolean, isCompact: boolean, editingFieldId: string | null,
 *   onStartEdit: (fieldId: string) => void, onStopEdit: () => void, onCommit: (patch: import('./field-input.js').FieldPatch) => void,
 *   onToggleSelected: (checked: boolean) => void, onDragStart: (event: DragEvent) => void, onDragEnd: () => void,
 *   onClassifyNotLead: (() => void) | null, onContextMenu?: (event: MouseEvent) => void, onOpen: (event: MouseEvent) => void }} props
 */
function BoardCard({
  objectMetadata, record, fields, groupField, relatedListConfigs, relatedLabels, isSelected, isCompact, editingFieldId,
  onStartEdit, onStopEdit, onCommit, onToggleSelected, onDragStart, onDragEnd, onClassifyNotLead, onContextMenu, onOpen,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const showBody = fields.length > 0 && (!isCompact || isExpanded);
  return html`<div
    class="board-card"
    data-board-card="true"
    data-record-id=${record.id}
    data-selected=${isSelected ? 'true' : undefined}
    data-editing=${editingFieldId ? 'true' : undefined}
    draggable=${editingFieldId ? 'false' : 'true'}
    onDragStart=${onDragStart}
    onDragEnd=${onDragEnd}
    onContextMenu=${onContextMenu}
    onClick=${(event) => {
      if (editingFieldId || /** @type {HTMLElement} */ (event.target).closest('a, label, button, .field-input-surface')) return;
      onOpen(event);
    }}
  >
    <div class="board-card-header">
      <${RecordChip} objectMetadata=${objectMetadata} record=${record} variant="transparent" isBold=${true} />
      ${objectMetadata.nameSingular === 'enrollmentOpportunity' && record.contactMarketingOptOut === true
        ? html`<span class="board-card-marketing-opt-out" title=${t('marketingConsent.boardChipTitle')} data-testid="board-card-marketing-opt-out">
            <${Icon} name="IconForbid" size=${12} stroke=${2} />${t('marketingConsent.boardChip')}
          </span>`
        : null}
      ${objectMetadata.nameSingular === 'enrollmentOpportunity' ? html`<${CardScheduleButton} record=${record} />` : null}
      ${onClassifyNotLead
        ? html`<span class="board-card-not-lead" title=${t('notLead.actionHint')}>
            <${LightIconButton} icon="IconUserOff" accent="tertiary" ariaLabel=${t('notLead.action')} onClick=${onClassifyNotLead} />
          </span>`
        : null}
      ${isCompact && fields.length > 0
        ? html`<span class="board-card-expand">
            <${LightIconButton} icon=${isExpanded ? 'IconEyeOff' : 'IconEye'} accent="tertiary"
              ariaLabel=${t(isExpanded ? 'boardTable.board.collapseCard' : 'boardTable.board.expandCard')} onClick=${() => setIsExpanded(!isExpanded)} />
          </span>`
        : null}
      <span class="board-card-checkbox" data-visible=${isSelected ? 'true' : undefined}>
        <${Checkbox} checked=${isSelected} onChange=${onToggleSelected} ariaLabel=${t('table.selectRow')} />
      </span>
    </div>
    ${showBody
      ? html`<div class="board-card-body">
          ${fields.map((field) => {
            const relatedListConfig = relatedListConfigs.get(field.id);
            return html`<${BoardCardField}
              key=${field.id}
              objectMetadata=${objectMetadata}
              field=${field}
              record=${record}
              isEditing=${editingFieldId === field.id}
              onStartEdit=${() => onStartEdit(field.id)}
              onStopEdit=${onStopEdit}
              onCommit=${onCommit}
            >${relatedListConfig
              ? html`<${BoardCardRelatedList} config=${relatedListConfig} record=${record} groupField=${groupField} relatedLabels=${relatedLabels} />`
              : null}<//>`;
          })}
        </div>`
      : null}
  </div>`;
}
